import { UserGroup } from './userGroup';

export interface GoogleStorageDocument {
	uuid: string;
	name: string;
	bucket: string;
	url: string;
	publicUrl: string;
	mimeType: string;
	size: number;
	created: string;
	updated: string;
}

export interface CompanyExternalUser {
	id: string;
	created: string;
	updated: string;
	firstName: string;
	lastName: string;
	preferredName?: string;
	preferredPronouns?: string;
	email: string;
	phone?: string;
	dateOfBirth?: string;
	groups?: UserGroup[];
	role?: string;
	status: string;
	notes?: string;
	lastContactDate?: string;
	companyId: string;
	userProfileId?: string;
	userProfile?: UserProfile;
	profilePictureUrl?: GoogleStorageDocument;
}

export interface Address {
	line1?: string;
	line2?: string;
	city?: string;
	state?: string;
	zip?: string;
	sameAsHomeAddress?: boolean;
}

export interface UserProfile {
	companyId?: string;
	userId?: string;
	companyExternalUserId?: string;
	homeAddress?: Address;
	shippingAddress?: Address;
	driversLicenseNumber?: string;
	driversLicenseState?: string;
	noDriversLicense?: boolean;
	hasVehicle?: boolean;
	willingToUseVehicleForProduction?: boolean;
	vehicleType?: string;
	vehicleLocation?: string;
	rate?: string;
	rateType?: string;
	dietaryRestrictions?: string;
	homeAirport?: string;
	passportNumber?: string;
	passportIssuingCountry?: string;
	passportExpirationDate?: string;
	noPassport?: boolean;
	globalEntry?: string;
	willingToTravelDomestically?: boolean;
	willingToTravelInternationally?: boolean;
	tsaPrecheck?: string;
	amtrackNumber?: string;
	deltaMileage?: string;
	unitedMileage?: string;
	preferredSeating?: string;
	starwoodMember?: string;
	emeraldClub?: string;
	hiltonNumber?: string;
	marriottBonvoyNumber?: string;
	preferredAirlines?: string[];
	preferredHotels?: string[];
	preferredCarRentals?: string[];
	preferredCarRentalCompanies?: string[];
	preferredRideSharing?: string[];
	preferredRideSharingApps?: string[];
	avisNumber?: string;
	budgetNumber?: string;
	certifications?: string[];
	isIphone?: boolean;
	phoneCarrier?: string;
	smsTextReady?: boolean;
	hasWhatsapp?: boolean;
	whatsappNumber?: string;
	hasWechat?: boolean;
	wechatNumber?: string;
	canReceivePackagesAtHomeAddress?: boolean;
	emergencyContactFirstName?: string;
	emergencyContactLastName?: string;
	emergencyContactPhone?: string;
	websiteUrl?: string;
	instagramHandle?: string;
	resumeProjectHistoryUploads?: GoogleStorageDocument[];
	currentJobStatus?: string;
	unionStatus?: string;
	unionName?: string;
	productionRole?: string;
	yearsOfExperiencePrimary?: string;
	rateTenOrTwelveHours?: string;
	isRateFixed?: boolean;
	chargesOvertimeTypically?: boolean;
	workReferenceFirstName?: string;
	workReferenceLastName?: string;
	workReferenceEmail?: string;
	workReferencePhone?: string;
	workReference2FirstName?: string;
	workReference2LastName?: string;
	workReference2Email?: string;
	workReference2Phone?: string;
	secondaryProductionRole?: string;
	secondaryRate?: string;
	secondaryRateType?: string;
	secondaryYearsOfExperience?: string;
	secondaryRateTenOrTwelveHours?: string;
	secondaryIsRateFixed?: boolean;
	secondaryChargesOvertimeTypically?: boolean;
	secondaryWorkReferenceFirstName?: string;
	secondaryWorkReferenceLastName?: string;
	secondaryWorkReferenceEmail?: string;
	secondaryWorkReferencePhone?: string;
	secondaryWorkReference2FirstName?: string;
	secondaryWorkReference2LastName?: string;
	secondaryWorkReference2Email?: string;
	secondaryWorkReference2Phone?: string;
	hasKit?: boolean;
	kitContents?: string;
	kitFee?: string;
	kitDocuments?: GoogleStorageDocument[];
	willingToBePhotographedOnSet?: boolean;
	shirtSize?: string;
	shoeSize?: string;
	localWorkLocations?: string[];
	employmentTypePreference?: string;
	wantFreelanceContact?: string;
	interestedInW2Positions?: string;
	interestedInInternship?: string;
	loanOutCompanyName?: string;
	loanOutCompanyContactName?: string;
	loanOutCompanyPhone?: string;
	loanOutCompanyEmail?: string;
	hasRepresentation?: boolean;
	representativeFullName?: string;
	representativeCompany?: string;
	representativePhone?: string;
	representativeEmail?: string;
	billThroughAgentCompany?: boolean;
	preferredPaymentMethods?: string[];
	profilePictureUrl?: GoogleStorageDocument;
}

export interface CreateCompanyExternalUserDTO {
	firstName: string;
	lastName: string;
	email: string;
	phone?: string;
	role?: string;
	permissions?: string;
	department?: string;
	status?: string;
	notes?: string;
	sendInvite?: boolean;
	customMessage?: string;
	groupIds?: string[];
}

export interface UpdateCompanyExternalUserDTO {
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	role?: string;
	permissions?: string;
	department?: string;
	status?: string;
	notes?: string;
	isActive?: boolean;
	lastContactDate?: string;
	homeAddress?: Address;
	shippingAddress?: Address;
	dateOfBirth?: string;
	driversLicenseNumber?: string;
	noDriversLicense?: boolean;
	driversLicenseState?: string;
	hasVehicle?: boolean;
	willingToUseVehicleForProduction?: boolean;
	vehicleType?: string;
	vehicleLocation?: string;
	rate?: string;
	rateType?: string;
	dietaryRestrictions?: string;
	homeAirport?: string;
	passportNumber?: string;
	passportIssuingCountry?: string;
	passportExpirationDate?: string;
	noPassport?: boolean;
	globalEntry?: string;
	willingToTravelDomestically?: boolean;
	willingToTravelInternationally?: boolean;
	tsaPrecheck?: string;
	amtrackNumber?: string;
	deltaMileage?: string;
	unitedMileage?: string;
	preferredSeating?: string;
	preferredName?: string;
	emergencyContactFirstName?: string;
	emergencyContactLastName?: string;
	emergencyContactPhone?: string;
	certifications?: string[];
	isIphone?: string;
	phoneCarrier?: string;
	smsTextReady?: boolean;
	hasWhatsapp?: boolean;
	whatsappNumber?: string;
	hasWechat?: boolean;
	wechatNumber?: string;
	starwoodMember?: string;
	hiltonMember?: string;
	hiltonNumber?: string;
	marriottBonvoyMember?: string;
	marriottBonvoyNumber?: string;
	emeraldClub?: string;
	avisNumber?: string;
	budgetNumber?: string;
	preferredAirlines?: string[];
	preferredHotels?: string[];
	preferredCarRentals?: string[];
	preferredRideSharingApps?: string[];
	preferredPronouns?: string;
	groupIds?: string[];
	canReceivePackagesAtHomeAddress?: boolean;
	websiteUrl?: string;
	instagramHandle?: string;
	resumeProjectHistoryUploads?: GoogleStorageDocument[];
	currentJobStatus?: string;
	wantFreelanceContact?: string;
	interestedInW2Positions?: string;
	interestedInInternship?: string;
	unionStatus?: string;
	unionName?: string;
	productionRole?: string;
	yearsOfExperiencePrimary?: string;
	rateTenOrTwelveHours?: string;
	isRateFixed?: boolean;
	chargesOvertimeTypically?: boolean;
	workReferenceFirstName?: string;
	workReferenceLastName?: string;
	workReferenceEmail?: string;
	workReferencePhone?: string;
	workReference2FirstName?: string;
	workReference2LastName?: string;
	workReference2Email?: string;
	workReference2Phone?: string;
	secondaryProductionRole?: string;
	secondaryRate?: string;
	secondaryRateType?: string;
	secondaryYearsOfExperience?: string;
	secondaryRateTenOrTwelveHours?: string;
	secondaryIsRateFixed?: boolean;
	secondaryChargesOvertimeTypically?: boolean;
	secondaryWorkReferenceFirstName?: string;
	secondaryWorkReferenceLastName?: string;
	secondaryWorkReferenceEmail?: string;
	secondaryWorkReferencePhone?: string;
	secondaryWorkReference2FirstName?: string;
	secondaryWorkReference2LastName?: string;
	secondaryWorkReference2Email?: string;
	secondaryWorkReference2Phone?: string;
	hasKit?: boolean;
	kitContents?: string;
	kitFee?: string;
	kitDocuments?: GoogleStorageDocument[];
	willingToBePhotographedOnSet?: boolean;
	shirtSize?: string;
	shoeSize?: string;
	localWorkLocations?: string[];
	employmentTypePreference?: string;
	loanOutCompanyName?: string;
	loanOutCompanyContactName?: string;
	loanOutCompanyPhone?: string;
	loanOutCompanyEmail?: string;
	hasRepresentation?: boolean;
	representativeFullName?: string;
	representativeCompany?: string;
	representativePhone?: string;
	representativeEmail?: string;
	billThroughAgentCompany?: boolean;
	preferredPaymentMethods?: string[];
	[key: string]: any; // Allow for additional form fields
}

export interface CompanyExternalUserFilter {
	companyId?: string;
	status?: string;
	isActive?: boolean;
	department?: string;
	searchText?: string;
	order?: string;
	direction?: string;
	page?: number;
	take?: number;
	where?: any;
	include?: any;
}

export interface CompanyExternalUsers {
	list: CompanyExternalUser[];
	count: number;
	filter: CompanyExternalUserFilter;
	initialized: boolean;
}

export interface VerifyOnboardingTokenInput {
	token: string;
	password?: string; // Optional for basic verification, required for full access
}

export interface OnboardingTokenVerificationResponse {
	valid: boolean;
	message?: string;
	externalUser?: CompanyExternalUser;
	sessionToken?: string;
	expiresAt?: string;
	error?: string;
}

export interface SubmitOnboardingFormInput {
	token: string;
	password: string; // NEW REQUIRED FIELD for two-factor authentication
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	role?: string;
	department?: string;
	notes?: string;
	homeAddress?: Address;
	shippingAddress?: Address;
	dateOfBirth?: string;
	driversLicense?: string;
	rate?: string;
	rateType?: string;
	dietaryRestrictions?: string;
	homeAirport?: string;
	passportNumber?: string;
	tsaPrecheck?: string;
	amtrackNumber?: string;
	deltaMileage?: string;
	preferredSeating?: string;
	starwoodMember?: string;
	emeraldClub?: string;
	preferredAirlines?: {
		alaskan: boolean;
		american: boolean;
		delta: boolean;
		frontier: boolean;
		klm: boolean;
		southwest: boolean;
		united: boolean;
		virgin: boolean;
	};
	[key: string]: any; // Allow for additional form fields
}

export interface SubmitExternalOnboardingFormInput {
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	role?: string;
	dateOfBirth?: string;
	profilePictureUrl?: GoogleStorageDocument;
	driversLicenseNumber?: string;
	noDriversLicense?: boolean;
	driversLicenseState?: string;
	hasVehicle?: boolean;
	willingToUseVehicleForProduction?: boolean;
	vehicleType?: string;
	vehicleLocation?: string;
	homeAddress?: Address;
	shippingAddress?: Address;
	rate?: string;
	rateType?: string;
	dietaryRestrictions?: string;
	homeAirport?: string;
	passportNumber?: string;
	passportIssuingCountry?: string;
	passportExpirationDate?: string;
	noPassport?: boolean;
	globalEntry?: string;
	willingToTravelDomestically?: boolean;
	willingToTravelInternationally?: boolean;
	tsaPrecheck?: string;
	amtrackNumber?: string;
	deltaMileage?: string;
	unitedMileage?: string;
	preferredSeating?: string;
	emergencyContactFirstName?: string;
	emergencyContactLastName?: string;
	emergencyContactPhone?: string;
	certifications?: string[];
	isIphone?: string;
	preferredName?: string;
	phoneCarrier?: string;
	smsTextReady?: boolean;
	hasWhatsapp?: boolean;
	whatsappNumber?: string;
	hasWechat?: boolean;
	wechatNumber?: string;
	starwoodMember?: string;
	hiltonMember?: string;
	hiltonNumber?: string;
	marriottBonvoyMember?: string;
	marriottBonvoyNumber?: string;
	emeraldClub?: string;
	avisNumber?: string;
	budgetNumber?: string;
	preferredAirlines?: string[];
	preferredHotels?: string[];
	preferredCarRentals?: string[];
	preferredCarRentalCompanies?: string[];
	preferredRideSharing?: string[];
	preferredRideSharingApps?: string[];
	canReceivePackagesAtHomeAddress?: boolean;
	websiteUrl?: string;
	instagramHandle?: string;
	resumeProjectHistoryUploads?: GoogleStorageDocument[];
	currentJobStatus?: string;
	wantFreelanceContact?: string;
	interestedInW2Positions?: string;
	interestedInInternship?: string;
	unionStatus?: string;
	unionName?: string;
	productionRole?: string;
	yearsOfExperiencePrimary?: string;
	rateTenOrTwelveHours?: string;
	isRateFixed?: boolean;
	chargesOvertimeTypically?: boolean;
	workReferenceFirstName?: string;
	workReferenceLastName?: string;
	workReferenceEmail?: string;
	workReferencePhone?: string;
	workReference2FirstName?: string;
	workReference2LastName?: string;
	workReference2Email?: string;
	workReference2Phone?: string;
	secondaryProductionRole?: string;
	secondaryRate?: string;
	secondaryRateType?: string;
	secondaryYearsOfExperience?: string;
	secondaryRateTenOrTwelveHours?: string;
	secondaryIsRateFixed?: boolean;
	secondaryChargesOvertimeTypically?: boolean;
	secondaryWorkReferenceFirstName?: string;
	secondaryWorkReferenceLastName?: string;
	secondaryWorkReferenceEmail?: string;
	secondaryWorkReferencePhone?: string;
	secondaryWorkReference2FirstName?: string;
	secondaryWorkReference2LastName?: string;
	secondaryWorkReference2Email?: string;
	secondaryWorkReference2Phone?: string;
	hasKit?: boolean;
	kitContents?: string;
	kitFee?: string;
	kitDocuments?: GoogleStorageDocument[];
	willingToBePhotographedOnSet?: boolean;
	shirtSize?: string;
	shoeSize?: string;
	localWorkLocations?: string[];
	employmentTypePreference?: string;
	loanOutCompanyName?: string;
	loanOutCompanyContactName?: string;
	loanOutCompanyPhone?: string;
	loanOutCompanyEmail?: string;
	hasRepresentation?: boolean;
	representativeFullName?: string;
	representativeCompany?: string;
	representativePhone?: string;
	representativeEmail?: string;
	billThroughAgentCompany?: boolean;
	preferredPaymentMethods?: string[];
	[key: string]: any; // Allow for additional form fields
}

export interface OnboardingSubmissionResponse {
	success: boolean;
	message?: string;
	error?: string;
}

export interface UpdateOnboardingProgressInput {
	token: string;
	password: string;
	userId: string;
	updates: UpdateCompanyExternalUserDTO;
}

export interface OnboardingProgressUpdateResponse {
	success: boolean;
	message?: string;
	error?: string;
	externalUser?: CompanyExternalUser;
}

export const CompanyExternalUserStatuses = {
	ACTIVE: {
		id: 'ACTIVE',
		display: 'Active',
	},
	INVITED: {
		id: 'INVITED',
		display: 'Invited',
	},
	INVITE_EXPIRED: {
		id: 'INVITE_EXPIRED',
		display: 'Invite Expired',
	},
	NOT_INVITED: {
		id: 'NOT_INVITED',
		display: 'Not Invited',
	},
	ARCHIVED: {
		id: 'ARCHIVED',
		display: 'Archived',
	},
};

export const CompanyExternalUserStatusDDOptions = Object.values(
	CompanyExternalUserStatuses
);
